/**
 * GTM Tracking Component
 *
 * Tracks clicks on any link that points to the site's default CTA
 * destination (the offerte page). The destination is provided by the
 * back end via the `data-default-cta-url` attribute on <body>, so every
 * link to that page is tracked automatically (navbar, CTA blocks, inline
 * links, CMS-created buttons) without per-link markup, and it stays
 * correct per language because the back end resolves the URL per site.
 *
 * One event is pushed for every such link, regardless of where it sits on
 * the page.
 */

declare global {
    interface Window {
        dataLayer: Record<string, unknown>[];
    }
}

/** Event name pushed to the dataLayer for an offerte-link click. */
const OFFERTE_EVENT = 'offerte_nav_click';

/**
 * Push an event to the GTM dataLayer.
 * Initializes the array if it does not yet exist.
 */
function pushDataLayer(event: Record<string, unknown>): void {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
}

/**
 * Normalize a URL to a comparable pathname: the pathname without a
 * trailing slash. Returns null if the value cannot be parsed.
 */
function toPathname(value: string): string | null {
    try {
        const { pathname } = new URL(value, window.location.origin);
        return pathname.replace(/\/+$/, '') || '/';
    } catch {
        return null;
    }
}

/**
 * Track clicks on any anchor that resolves to the default CTA URL.
 * Delegated on the document so links added after load are covered too.
 */
function initDefaultCtaClickTracking(): void {
    const raw = document.body.dataset.defaultCtaUrl;
    if (!raw) return;

    const targetPath = toPathname(raw);
    if (!targetPath) return;

    document.addEventListener('click', (e: MouseEvent) => {
        const el = e.target as Element | null;
        const anchor = el?.closest('a[href]') as HTMLAnchorElement | null;
        if (!anchor) return;

        // Only same-origin links can match; anchor.href is always absolute.
        if (anchor.origin !== window.location.origin) return;
        if (toPathname(anchor.href) !== targetPath) return;

        pushDataLayer({
            event: OFFERTE_EVENT,
            link_url: anchor.href,
            link_text: (anchor.innerText || '').trim(),
            link_classes: anchor.className || '',
            link_location: window.location.pathname,
        });
    });
}

/**
 * Initialize all GTM tracking handlers.
 */
export function initGtmTracking(): void {
    initDefaultCtaClickTracking();
}
